import { Code, ConnectError, createClient } from '@connectrpc/connect';
import type * as THREE from 'three';
import {
  CadFileKind,
  ChangeType,
  VersionControlService,
} from '@puppycad/proto';
import type {
  CadNode,
  CommitInfo,
} from '@puppycad/proto';
import {
  assertImportedDocumentCanPersist,
  createAssemblyDocumentWithRepositoryPuppyNodeWhere,
  createVisibleModelExportRoot,
  exportPuppyPackage,
  isAssemblyOfflinePackageFile,
  normalizePuppyExportFileBaseName,
  serializeAssemblyDocument,
} from '@puppycad/cad-core';
import type {
  AssemblyMateDocumentJSON,
  PuppyDatumExtensionInput,
  PuppyExportAssemblySemanticSource,
  PuppyVisibleModelExportRoot,
} from '@puppycad/cad-core';
import { grpcWebTransport } from '@/services/api/transport/grpc-web-transport';
import {
  assertCommitTargetContext,
  createCommitFilesToBranchRequestStream,
  DEFAULT_COMMIT_CHUNK_SIZE,
  normalizeRepositoryPath,
} from '@/services/commit-files-to-branch';
import type {
  CommitFilesToBranchFilePayload,
  CommitFilesToBranchInvoker,
} from '@/services/commit-files-to-branch';
import type {
  CommitCurrentAssemblyWorkspaceInput,
  ExportAssemblyPackageInvoker,
} from '../../../types/services';

export type {
  CommitCurrentAssemblyWorkspaceInput,
} from '../../../types/services';

const ASSEMBLY_METADATA_FILE_ID = '_assembly.json';

let assemblyPackageFileNameSequence = 0;

/**
 * 装配离线包文件名生成函数。
 */
type AssemblyPackageFileNameFactory = (rootNode: CadNode) => string;

/**
 * 装配离线包提交目标。
 */
interface AssemblyPackageCommitTarget {
  /** 仓库内相对路径。 */
  readonly path: string;
  /** 文件业务 UUID。 */
  readonly fileUuid: string;
  /** 文件变更类型。 */
  readonly changeType: ChangeType;
}

/**
 * 提交当前装配工作区后的结果。
 */
export interface CommitCurrentAssemblyWorkspaceResult {
  /** 后端返回的新提交信息。 */
  readonly commit: CommitInfo;
  /** 本次提交的 `workAssemble` 仓库路径。 */
  readonly assemblyPath: string;
  /** 本次提交的 `workAssemble` 字节数。 */
  readonly assemblyByteLength: number;
  /** 本次提交的离线包仓库路径；空装配体仅提交 `workAssemble` 时为空字符串。 */
  readonly packagePath: string;
  /** 本次提交的离线包字节数；空装配体仅提交 `workAssemble` 时为 0。 */
  readonly packageByteLength: number;
}

/**
 * 创建装配工作区提交服务的可选依赖。
 */
export interface CreateAssemblyWorkspaceCommitServiceOptions {
  /** 流式提交函数；测试可注入 mock，生产默认使用 VersionControlService。 */
  readonly commitFilesToBranch?: CommitFilesToBranchInvoker;
  /** 装配 `.puppy` 打包函数；测试可注入固定字节，生产默认使用 cad-core。 */
  readonly exportAssemblyPackage?: ExportAssemblyPackageInvoker;
  /** 装配离线包文件名生成函数；测试可注入确定性名称。 */
  readonly createPackageFileName?: AssemblyPackageFileNameFactory;
  /** 提交 chunk 大小；默认 1MB。 */
  readonly chunkSize?: number;
}

/**
 * 装配工作区提交服务。
 */
export interface AssemblyWorkspaceCommitService {
  /**
   * 提交当前根装配的工作文档和一份新的离线包。
   *
   * @param input 装配工作区提交输入。
   * @returns 后端创建的新提交和本次写入文件信息。
   */
  commitCurrentAssemblyWorkspace(
    input: CommitCurrentAssemblyWorkspaceInput,
  ): Promise<CommitCurrentAssemblyWorkspaceResult>;
}

/**
 * 按两位宽度补齐日期时间片段。
 *
 * @param value 日期时间数值。
 * @returns 两位字符串。
 */
function padDatePart(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * 格式化装配离线包文件名中的时间戳。
 *
 * @param date 当前时间。
 * @returns `yyyyMMdd-HHmmss` 格式时间戳。
 */
function formatAssemblyPackageTimestamp(date: Date): string {
  const year = date.getFullYear().toString();
  const month = padDatePart(date.getMonth() + 1);
  const day = padDatePart(date.getDate());
  const hour = padDatePart(date.getHours());
  const minute = padDatePart(date.getMinutes());
  const second = padDatePart(date.getSeconds());

  return `${year}${month}${day}-${hour}${minute}${second}`;
}

/**
 * 生成离线包文件名中的短唯一后缀。
 *
 * @returns 当前运行时内尽量短且不重复的后缀。
 */
function createAssemblyPackageUniqueSuffix(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID().replace(/-/gu, '').slice(0, 8).toLowerCase();
  }

  assemblyPackageFileNameSequence += 1;
  const sequence = assemblyPackageFileNameSequence.toString(36).padStart(4, '0');
  const randomText = Math.random().toString(36).slice(2, 6);

  return `${sequence}${randomText}`;
}

/**
 * 解析根装配用于导出和文件命名的基础名称。
 *
 * @param rootNode OpenProject 根装配节点。
 * @returns 优先来自实例名的基础名称。
 */
function resolveAssemblyPackageBaseName(rootNode: CadNode): string {
  const instanceName = rootNode.instanceName.trim();
  if (instanceName) {
    return instanceName;
  }

  const repositoryIdText = rootNode.repositoryId > 0n ? rootNode.repositoryId.toString() : '';
  return repositoryIdText ? `assembly-${repositoryIdText}` : 'assembly';
}

/**
 * 创建装配保存新建离线包时使用的文件名。
 *
 * @param rootNode OpenProject 根装配节点。
 * @returns 唯一 `.puppy` 文件名。
 */
export function createAssemblyWorkspacePackageFileName(rootNode: CadNode): string {
  const baseName = normalizePuppyExportFileBaseName(resolveAssemblyPackageBaseName(rootNode));
  const timestamp = formatAssemblyPackageTimestamp(new Date());
  const uniqueSuffix = createAssemblyPackageUniqueSuffix();

  return `${baseName}-${timestamp}-${uniqueSuffix}.puppy`;
}

/**
 * 读取根装配 `_assembly.json` 的文件 UUID。
 *
 * @param rootNode OpenProject 根装配节点。
 * @returns 已存在文件 UUID；缺失时返回空字符串，由后端按路径和 base commit 处理。
 */
function resolveAssemblyMetadataFileUuid(rootNode: CadNode): string {
  const assemblyFile = rootNode.files.find((file) => (
    normalizeRepositoryPath(file.fileId) === ASSEMBLY_METADATA_FILE_ID
  ));

  return assemblyFile?.uuid.trim() ?? '';
}

/**
 * 格式化离线包候选文件信息。
 *
 * @param file 根装配文件描述。
 * @returns 可定位后端文件快照的摘要。
 */
function formatAssemblyPackageCandidate(file: CadNode['files'][number]): string {
  return `fileId=${file.fileId}, uuid=${file.uuid}, kind=${file.kind}`;
}

/**
 * 解析本次装配离线包提交目标。
 *
 * 设计说明：如果后端快照中已经存在唯一且带 UUID 的 `.puppy`，新导出的包应覆盖这个文件身份，
 * 避免同一装配提交后出现多份离线包。没有可复用 UUID 时才新增文件，让后端创建首次包身份。
 *
 * @param rootNode OpenProject 根装配节点。
 * @param createPackageFileName 新包文件名生成函数。
 * @returns 本次离线包 change 帧应使用的路径、UUID 与变更类型。
 */
function resolveAssemblyPackageCommitTarget(
  rootNode: CadNode,
  createPackageFileName: AssemblyPackageFileNameFactory,
): AssemblyPackageCommitTarget {
  const packageFiles = rootNode.files.filter(isAssemblyOfflinePackageFile);
  if (packageFiles.length > 1) {
    throw new Error(
      '当前根装配存在多个离线包，无法确定覆盖目标：'
      + packageFiles.map(formatAssemblyPackageCandidate).join('; '),
    );
  }

  const existingPackage = packageFiles[0];
  const existingPackageUuid = existingPackage?.uuid.trim() ?? '';
  if (existingPackage && existingPackageUuid) {
    const packagePath = normalizeRepositoryPath(existingPackage.fileId);
    if (!packagePath) {
      throw new Error('装配离线包文件路径为空，无法覆盖旧包');
    }

    return {
      path: packagePath,
      fileUuid: existingPackageUuid,
      changeType: ChangeType.MODIFIED,
    };
  }

  const packagePath = normalizeRepositoryPath(createPackageFileName(rootNode));
  if (!packagePath) {
    throw new Error('装配离线包文件名为空，无法提交');
  }

  return {
    path: packagePath,
    fileUuid: '',
    changeType: ChangeType.ADDED,
  };
}

/**
 * 校验装配工作区提交是否仍处于有效生命周期。
 *
 * 设计说明：装配提交会先导出 `.puppy`，再发起流式 RPC。页面卸载或提交请求被替换时，
 * 应尽早停止后续步骤，避免继续生成大字节载荷或把晚返回结果写回已销毁页面。
 *
 * @param signal 调用方传入的取消信号。
 */
function assertAssemblyWorkspaceCommitSignalActive(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new ConnectError('装配提交请求已取消', Code.Canceled);
  }
}

/**
 * 释放提交前模型探测产生的临时导出根。
 *
 * 设计说明：`createVisibleModelExportRoot` 会创建临时 Group/Mesh 快照，但 Mesh 复用源几何体和材质，
 * 因此这里只解除对象树引用，不 dispose 借用资源，避免误释放仍挂在 Viewer 中的模型资源。
 *
 * @param exportRoot 模型导出探测结果。
 */
function releaseAssemblyExportProbeRoot(exportRoot: PuppyVisibleModelExportRoot): void {
  exportRoot.root.clear();
}

/**
 * 判断当前装配场景是否存在可进入 `.puppy` 打包流程的模型。
 *
 * 设计说明：
 * 1. `workAssemble` 只表达协同结构，可能存在 direct child，但当前场景已经没有可导出的模型；
 * 2. 空装配体只允许跳过离线包导出，不能吞掉真实模型的 BRep/拓扑打包错误；
 * 3. 因此这里复用 cad-core 的可见模型导出根构建逻辑，以“当前场景是否产生可导出 mesh”为准。
 *
 * @param input 当前装配导出探测输入。
 * @returns 存在可导出 mesh 或几何绑定时返回 `true`。
 */
function hasCurrentAssemblyExportableModel(input: {
  /** 当前 Viewer 场景根。 */
  readonly sceneRoot: THREE.Object3D;
  /** 当前装配导出使用的权威设计树语义源。 */
  readonly assemblySemanticSource?: PuppyExportAssemblySemanticSource | null;
  /** 当前装配体标准 Mate 文档 JSON。 */
  readonly mates?: AssemblyMateDocumentJSON;
  /** 当前文档级基准点、基准轴、基准面数据。 */
  readonly datumExtension?: PuppyDatumExtensionInput | null;
}): boolean {
  const exportRoot = createVisibleModelExportRoot(input.sceneRoot, {
    ...(input.assemblySemanticSource ? { assemblySemanticSource: input.assemblySemanticSource } : {}),
    ...(input.mates ? { mates: input.mates } : {}),
    ...(input.datumExtension ? { datumExtension: input.datumExtension } : {}),
  });

  try {
    const hasExportedMesh = exportRoot.diagnostics.exportedMeshCount > 0;
    const hasGeometryBinding = exportRoot.nodeBindings.some((binding) => binding.geometryObjectIds.length > 0);
    const hasTopologyAsset = exportRoot.bodyTopologyAssets.length > 0;

    return hasExportedMesh || hasGeometryBinding || hasTopologyAsset;
  } finally {
    releaseAssemblyExportProbeRoot(exportRoot);
  }
}

/**
 * 创建装配工作区提交服务。
 *
 * @param options 可选依赖注入。
 * @returns 装配工作区提交服务。
 */
export function createAssemblyWorkspaceCommitService(
  options: CreateAssemblyWorkspaceCommitServiceOptions = {},
): AssemblyWorkspaceCommitService {
  const commitFilesToBranch = options.commitFilesToBranch
    ?? ((stream, invokeOptions) => createClient(VersionControlService, grpcWebTransport)
      .commitFilesToBranch(stream, invokeOptions?.signal ? { signal: invokeOptions.signal } : undefined));
  const exportAssemblyPackage = options.exportAssemblyPackage
    ?? ((input) => exportPuppyPackage({
      fileBaseName: input.fileBaseName,
      documentType: 'assembly',
      sceneRoot: input.sceneRoot,
      ...(input.assemblySemanticSource ? { assemblySemanticSource: input.assemblySemanticSource } : {}),
      projectModelSource: input.projectModelSource,
      ...(input.mates ? { mates: input.mates } : {}),
      ...(input.datumExtension ? { datumExtension: input.datumExtension } : {}),
      ...(input.linearComponentPatternDocument
        ? { linearComponentPatternDocument: input.linearComponentPatternDocument }
        : {}),
      //------ 草图离线包定义持久化 开始------
      ...(input.sketchExtensionEntries ? { sketchExtensionEntries: input.sketchExtensionEntries } : {}),
      ...(input.sketchEntityReferenceRecords
        ? { sketchEntityReferenceRecords: input.sketchEntityReferenceRecords }
        : {}),
      //------ 草图离线包定义持久化 结束------
      //------ 特征离线包定义持久化 开始------
      ...(input.featureExtensionInput ? { featureExtensionInput: input.featureExtensionInput } : {}),
      //------ 特征离线包定义持久化 结束------
      ...(input.sectionViewSnapshot ? { sectionViewSnapshot: input.sectionViewSnapshot } : {}),
      ...(input.workerManager ? { workerManager: input.workerManager } : {}),
    }));
  const createPackageFileName = options.createPackageFileName ?? createAssemblyWorkspacePackageFileName;
  const chunkSize = options.chunkSize ?? DEFAULT_COMMIT_CHUNK_SIZE;

  return {
    async commitCurrentAssemblyWorkspace(
      input: CommitCurrentAssemblyWorkspaceInput,
    ): Promise<CommitCurrentAssemblyWorkspaceResult> {
      const message = input.message.trim();
      if (!message) {
        throw new Error('提交说明不能为空');
      }
      if (input.rootNode.type !== CadFileKind.ASSEMBLY) {
        throw new Error('当前根节点不是装配体，无法提交装配工作区');
      }
      assertCommitTargetContext(input.rootNode, '装配');
      assertImportedDocumentCanPersist(input.projectModelLoadResult);

      const baseCommitId = input.baseCommitId.trim();
      if (!baseCommitId) {
        throw new Error('装配提交缺少 baseCommitId，无法提交工作区');
      }

      assertAssemblyWorkspaceCommitSignalActive(input.signal);
      let assemblyDocumentForCommit = input.assemblyDocument;
      let packageFile: CommitFilesToBranchFilePayload | null = null;
      let packagePath = '';
      let packageByteLength = 0;

      if (hasCurrentAssemblyExportableModel({
        sceneRoot: input.sceneRoot,
        ...(input.assemblySemanticSource ? { assemblySemanticSource: input.assemblySemanticSource } : {}),
        ...(input.mates ? { mates: input.mates } : {}),
        ...(input.datumExtension ? { datumExtension: input.datumExtension } : {}),
      })) {
        const exportResult = await exportAssemblyPackage({
          fileBaseName: resolveAssemblyPackageBaseName(input.rootNode),
          sceneRoot: input.sceneRoot,
          ...(input.assemblySemanticSource ? { assemblySemanticSource: input.assemblySemanticSource } : {}),
          projectModelSource: {
            loadResult: input.projectModelLoadResult,
            assemblyDocument: input.assemblyDocument,
          },
          ...(input.mates ? { mates: input.mates } : {}),
          ...(input.datumExtension ? { datumExtension: input.datumExtension } : {}),
          ...(input.linearComponentPatternDocument
            ? { linearComponentPatternDocument: input.linearComponentPatternDocument }
            : {}),
          //------ 草图离线包定义持久化 开始------
          ...(input.sketchExtensionEntries ? { sketchExtensionEntries: input.sketchExtensionEntries } : {}),
          ...(input.sketchEntityReferenceRecords
            ? { sketchEntityReferenceRecords: input.sketchEntityReferenceRecords }
            : {}),
          //------ 草图离线包定义持久化 结束------
          //------ 特征离线包定义持久化 开始------
          ...(input.featureExtensionInput ? { featureExtensionInput: input.featureExtensionInput } : {}),
          //------ 特征离线包定义持久化 结束------
          ...(input.sectionViewSnapshot ? { sectionViewSnapshot: input.sectionViewSnapshot } : {}),
          ...(input.workerManager ? { workerManager: input.workerManager } : {}),
        });
        assertAssemblyWorkspaceCommitSignalActive(input.signal);
        const packageTarget = resolveAssemblyPackageCommitTarget(input.rootNode, createPackageFileName);

        assemblyDocumentForCommit = createAssemblyDocumentWithRepositoryPuppyNodeWhere({
          document: input.assemblyDocument,
          rootNodeKey: input.projectModelLoadResult.root.nodeKey,
          packageFileId: packageTarget.path,
          identities: exportResult.rootDirectOccurrenceIdentities,
        });
        packageFile = {
          path: packageTarget.path,
          fileUuid: packageTarget.fileUuid,
          changeType: packageTarget.changeType,
          bytes: exportResult.bytes,
        };
        packagePath = packageTarget.path;
        packageByteLength = exportResult.bytes.byteLength;
      }
      assertAssemblyWorkspaceCommitSignalActive(input.signal);

      // repository node_where 依赖最终包路径，必须在包目标解析和 clone 桥接完成后再序列化。
      const assemblyText = serializeAssemblyDocument(assemblyDocumentForCommit);
      const assemblyBytes = new TextEncoder().encode(assemblyText);
      const files: CommitFilesToBranchFilePayload[] = [
        {
          path: ASSEMBLY_METADATA_FILE_ID,
          fileUuid: resolveAssemblyMetadataFileUuid(input.rootNode),
          changeType: ChangeType.MODIFIED,
          bytes: assemblyBytes,
        },
        ...(packageFile ? [packageFile] : []),
      ];

      const commit = await commitFilesToBranch(createCommitFilesToBranchRequestStream({
        repositoryId: input.rootNode.repositoryId,
        branchId: input.rootNode.branchId,
        message,
        baseCommitId,
        chunkSize,
        signal: input.signal,
        files,
      }), { signal: input.signal });

      return {
        commit,
        assemblyPath: ASSEMBLY_METADATA_FILE_ID,
        assemblyByteLength: assemblyBytes.byteLength,
        packagePath,
        packageByteLength,
      };
    },
  };
}
